"use client";

import * as React from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const switchVariants = cva(
  "relative peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        // Default variant
        default: "data-[state=checked]:bg-primary/50 data-[state=unchecked]:bg-gray-300",
        
        // Primary variants
        primary: "data-[state=checked]:bg-primary/50 data-[state=unchecked]:bg-gray-300",
        "primary-light": "data-[state=checked]:bg-primary-light/50 data-[state=unchecked]:bg-gray-300",
        "primary-dark": "data-[state=checked]:bg-primary-dark/50 data-[state=unchecked]:bg-gray-300",
        
        // Secondary variants
        secondary: "data-[state=checked]:bg-secondary/50 data-[state=unchecked]:bg-gray-300",
        "secondary-light": "data-[state=checked]:bg-secondary-light/50 data-[state=unchecked]:bg-gray-300",
        "secondary-dark": "data-[state=checked]:bg-secondary-dark/50 data-[state=unchecked]:bg-gray-300",
        
        // Info variants
        info: "data-[state=checked]:bg-info/50 data-[state=unchecked]:bg-gray-300",
        "info-light": "data-[state=checked]:bg-info-light/50 data-[state=unchecked]:bg-gray-300",
        "info-dark": "data-[state=checked]:bg-info-dark/50 data-[state=unchecked]:bg-gray-300",
        
        // Success variants
        success: "data-[state=checked]:bg-success/50 data-[state=unchecked]:bg-gray-300",
        "success-light": "data-[state=checked]:bg-success-light/50 data-[state=unchecked]:bg-gray-300",
        "success-dark": "data-[state=checked]:bg-success-dark/50 data-[state=unchecked]:bg-gray-300",
        
        // Warning variants
        warning: "data-[state=checked]:bg-warning/50 data-[state=unchecked]:bg-gray-300",
        "warning-light": "data-[state=checked]:bg-warning-light/50 data-[state=unchecked]:bg-gray-300",
        "warning-dark": "data-[state=checked]:bg-warning-dark/50 data-[state=unchecked]:bg-gray-300",
        
        // Error variants
        error: "data-[state=checked]:bg-error/50 data-[state=unchecked]:bg-gray-300",
        "error-light": "data-[state=checked]:bg-error-light/50 data-[state=unchecked]:bg-gray-300",
        "error-dark": "data-[state=checked]:bg-error-dark/50 data-[state=unchecked]:bg-gray-300",
        
        // Action variants
        action: "data-[state=checked]:bg-action/50 data-[state=unchecked]:bg-gray-300",
        "action-selected": "data-[state=checked]:bg-action-selected/50 data-[state=unchecked]:bg-gray-300",
      },
      size: {
        default: "h-[16px] w-[40px]",
        sm: "h-[12px] w-[32px]",
        lg: "h-[20px] w-[48px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const thumbVariants = cva(
  "pointer-events-none absolute top-1/2 -translate-y-1/2 block rounded-full bg-white shadow-lg ring-0 transition-all duration-200 ease-in-out",
  {
    variants: {
      variant: {
        // Default variant
        default: "data-[state=checked]:bg-primary data-[state=unchecked]:bg-white",
        
        // Primary variants
        primary: "data-[state=checked]:bg-primary data-[state=unchecked]:bg-white",
        "primary-light": "data-[state=checked]:bg-primary-light data-[state=unchecked]:bg-white",
        "primary-dark": "data-[state=checked]:bg-primary-dark data-[state=unchecked]:bg-white",
        
        // Secondary variants
        secondary: "data-[state=checked]:bg-secondary data-[state=unchecked]:bg-white",
        "secondary-light": "data-[state=checked]:bg-secondary-light data-[state=unchecked]:bg-white",
        "secondary-dark": "data-[state=checked]:bg-secondary-dark data-[state=unchecked]:bg-white",
        
        // Info variants
        info: "data-[state=checked]:bg-info data-[state=unchecked]:bg-white",
        "info-light": "data-[state=checked]:bg-info-light data-[state=unchecked]:bg-white",
        "info-dark": "data-[state=checked]:bg-info-dark data-[state=unchecked]:bg-white",
        
        // Success variants
        success: "data-[state=checked]:bg-success data-[state=unchecked]:bg-white",
        "success-light": "data-[state=checked]:bg-success-light data-[state=unchecked]:bg-white",
        "success-dark": "data-[state=checked]:bg-success-dark data-[state=unchecked]:bg-white",
        
        // Warning variants
        warning: "data-[state=checked]:bg-warning data-[state=unchecked]:bg-white",
        "warning-light": "data-[state=checked]:bg-warning-light data-[state=unchecked]:bg-white",
        "warning-dark": "data-[state=checked]:bg-warning-dark data-[state=unchecked]:bg-white",
        
        // Error variants
        error: "data-[state=checked]:bg-error data-[state=unchecked]:bg-white",
        "error-light": "data-[state=checked]:bg-error-light data-[state=unchecked]:bg-white",
        "error-dark": "data-[state=checked]:bg-error-dark data-[state=unchecked]:bg-white",
        
        // Action variants
        action: "data-[state=checked]:bg-action data-[state=unchecked]:bg-white",
        "action-selected": "data-[state=checked]:bg-action-selected data-[state=unchecked]:bg-white",
      },
      size: {
        default: "h-[24px] w-[24px] data-[state=checked]:translate-x-[20px] data-[state=unchecked]:translate-x-[-3px]",
        sm: "h-[18px] w-[18px] data-[state=checked]:translate-x-[16px] data-[state=unchecked]:translate-x-[-2px]",
        lg: "h-[28px] w-[28px] data-[state=checked]:translate-x-[24px] data-[state=unchecked]:translate-x-[-4px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface SwitchProps 
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
    VariantProps<typeof switchVariants> {
}

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(({ className, variant, size, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(switchVariants({ variant, size, className }))}
    {...props}
    ref={ref}
  >
    <SwitchPrimitives.Thumb
      className={cn(thumbVariants({ variant, size }))}
    />
  </SwitchPrimitives.Root>
));
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
